import { useState, useEffect, useRef } from 'react';
import { useTranslations } from '../context/TranslationContext';
import useAnalytics from '../hooks/useAnalytics';

interface FAQItemProps {
  question: string;
  answer: string;
  index: number;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, index }) => {
  const { trackEvent } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const openTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      trackEvent('faq_interaction', {
        action_type: 'open',
        question_index: index,
        question_text: question
      });
      openTimeRef.current = Date.now();
    } else if (openTimeRef.current) {
      const timeSpentMs = Date.now() - openTimeRef.current;
      const timeSpentSeconds = Math.round(timeSpentMs / 1000);
      if (timeSpentSeconds >= 2) {
        trackEvent('faq_engagement', {
          question_index: index,
          question_text: question,
          time_spent_seconds: timeSpentSeconds
        });
      }
      openTimeRef.current = null;
    }
  }, [isOpen, index, question, trackEvent]);

  const num = String(index + 1).padStart(2, '0');

  return (
    <div className="hairline">
      <button
        className="w-full py-7 flex items-start gap-8 text-left hover:bg-white/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="number-tag pt-1 flex-shrink-0">{num}</span>
        <div className="flex-1">
          <h3 className="text-lg md:text-xl font-light tracking-tight pr-8">{question}</h3>
        </div>
        <span
          className={`flex-shrink-0 text-2xl font-light leading-none pt-1 transition-transform duration-400 ${
            isOpen ? 'rotate-45' : ''
          }`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
        >
          +
        </span>
      </button>
      {/* grid-rows 0fr -> 1fr animates to the answer's natural height: no
          magic max-height to clip a long answer, and the easing tracks the
          real distance instead of an arbitrary 600px. */}
      <div
        className={`grid transition-all duration-500 ${
          isOpen ? 'grid-rows-[1fr] opacity-100 pt-4 pb-8' : 'grid-rows-[0fr] opacity-0'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
      >
        <div className="overflow-hidden">
          <div className="pl-16 pr-16 text-mute text-[15px] leading-[1.8] font-light">
            {answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const { t } = useTranslations();
  const { trackEvent } = useAnalytics();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEvent('faq_section_view', {
              timestamp: new Date().toISOString(),
              visible_questions: t.faq.items.length
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [trackEvent, t.faq.items.length]);

  return (
    <section id="faq" className="py-32 bg-cream" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-reveal>
          <div className="lg:col-span-4">
            <div className="eyebrow mb-4">02 / {t.faq.title}</div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="display text-[42px] md:text-[56px] mb-6">{t.faq.title}</h2>
            <p className="text-mute text-base leading-[1.8] max-w-2xl font-light">
              {t.faq.description}
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {t.faq.items.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
